import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSubmitMerchantRequest } from "@/api/hooks/mutations";
import { ApiError } from "@/api/client";
import { Button, buttonClass } from "@/components/ui/Button";
import { Field, fieldInputClass } from "@/components/ui/Field";
import { ChipInput } from "@/components/ui/ChipInput";
import { SegmentedToggle } from "@/components/ui/SegmentedToggle";

const schema = z.object({
  contact_name: z.string().min(2, "Required"),
  contact_email: z.string().email("Enter a valid email"),
  contact_phone: z.string().optional().or(z.literal("")),
  contact_company: z.string().min(2, "Required"),
  contact_website: z.string().optional().or(z.literal("")),
  preferred_contact: z.enum(["email", "phone", "either"]).optional(),
  position_title: z.string().min(2, "Required"),
  seniority: z.enum(["junior", "middle", "senior", "lead"]),
  engagement: z.enum(["full_time", "part_time", "project_based"]),
  location: z.string().optional().or(z.literal("")),
  budget: z.string().optional().or(z.literal("")),
  start_date: z.string().optional().or(z.literal("")),
  description: z.string().min(10, "A short description helps"),
  website: z.string().max(0).optional().or(z.literal("")),
});

type Input = z.infer<typeof schema>;

export function MerchantRequestPage() {
  const submit = useSubmitMerchantRequest();
  const [serviceType, setServiceType] = useState<"outstaffing" | "recruiting">("outstaffing");
  const [skills, setSkills] = useState<string[]>([]);
  const [skillsInvalid, setSkillsInvalid] = useState(false);
  const [topError, setTopError] = useState("");
  const [done, setDone] = useState(false);

  const form = useForm<Input>({
    resolver: zodResolver(schema),
    defaultValues: { seniority: "senior", engagement: "full_time" },
  });

  async function onSubmit(values: Input) {
    setTopError("");
    if (values.website) {
      setDone(true);
      return;
    }
    if (!skills.length) {
      setSkillsInvalid(true);
      setTopError("Please add at least one required skill.");
      return;
    }
    setSkillsInvalid(false);
    try {
      await submit.mutateAsync({
        service_type: serviceType,
        contact_name: values.contact_name,
        contact_email: values.contact_email,
        contact_phone: values.contact_phone || null,
        contact_company: values.contact_company,
        contact_website: values.contact_website || null,
        preferred_contact: values.preferred_contact ?? null,
        position_title: values.position_title,
        seniority: values.seniority,
        skills,
        engagement: values.engagement,
        location: values.location || null,
        budget: values.budget || null,
        start_date: values.start_date || null,
        description: values.description,
        website: null,
        turnstile_token: null,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429)
          setTopError("Too many submissions — please try again in a minute.");
        else setTopError(err.message || "Could not submit your request.");
      } else {
        setTopError("Network error. Please try again.");
      }
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-[1100px] px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-2xl border border-border bg-surface p-6 text-center sm:p-10">
          <h1 className="text-2xl font-bold">Request received</h1>
          <p className="mt-2 text-text-muted">
            Thanks — we'll get back to you within 24 hours.
          </p>
          <div className="mt-5 flex justify-center">
            <Link to="/" className={`${buttonClass()} w-full sm:w-auto`}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-10 sm:px-6 sm:py-12">
      <div className="mb-3 flex flex-wrap gap-x-1 text-sm text-text-muted">
        <Link to="/" className="hover:text-text">
          Home
        </Link>{" "}
        › Hire talent › New request
      </div>

      <div className="mb-7">
        <h1 className="text-2xl font-bold tracking-tight">Request a specialist</h1>
        <p className="mt-1 text-sm text-text-muted">
          Tell us about the role. We'll get back to you within 24 hours.
        </p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="grid items-start gap-6 lg:grid-cols-[1fr_320px]">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...form.register("website")}
          className="absolute -left-[9999px]"
          aria-hidden
        />

        <div className="flex flex-col gap-5">
          <Card title="How would you like to engage?" hint="Choose the model that fits.">
            <SegmentedToggle
              value={serviceType}
              onChange={setServiceType}
              options={[
                {
                  value: "outstaffing",
                  title: "Outstaffing",
                  description:
                    "The specialist stays on our payroll and works full-time on your team. We handle contracts, taxes, equipment and HR.",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                  ),
                },
                {
                  value: "recruiting",
                  title: "Recruiting",
                  description:
                    "We source and shortlist candidates for you to hire directly. One-time placement fee, no ongoing relationship.",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                  ),
                },
              ]}
            />
          </Card>

          <Card title="Your contact details" hint="We'll reach out here.">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label="Full name"
                required
                error={form.formState.errors.contact_name?.message}
              >
                <input
                  type="text"
                  placeholder="e.g. Anna Chen"
                  {...form.register("contact_name")}
                  className={fieldInputClass(!!form.formState.errors.contact_name)}
                />
              </Field>
              <Field
                label="Work email"
                required
                error={form.formState.errors.contact_email?.message}
              >
                <input
                  type="email"
                  placeholder="anna@yourcompany.com"
                  {...form.register("contact_email")}
                  className={fieldInputClass(!!form.formState.errors.contact_email)}
                />
              </Field>
              <Field
                label="Company"
                required
                error={form.formState.errors.contact_company?.message}
              >
                <input
                  type="text"
                  placeholder="e.g. Acme Capital"
                  {...form.register("contact_company")}
                  className={fieldInputClass(!!form.formState.errors.contact_company)}
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel"
                  placeholder="+1 555 0100"
                  {...form.register("contact_phone")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Company website">
                <input
                  type="text"
                  placeholder="acme.com"
                  {...form.register("contact_website")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Preferred contact method">
                <select
                  {...form.register("preferred_contact")}
                  className={fieldInputClass()}
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="either">Either</option>
                </select>
              </Field>
            </div>
          </Card>

          <Card title="Role details" hint="The clearer the brief, the faster we can match.">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label="Position title"
                required
                error={form.formState.errors.position_title?.message}
              >
                <input
                  type="text"
                  placeholder="e.g. Senior Backend Engineer"
                  {...form.register("position_title")}
                  className={fieldInputClass(!!form.formState.errors.position_title)}
                />
              </Field>
              <Field label="Seniority" required>
                <select {...form.register("seniority")} className={fieldInputClass()}>
                  <option value="junior">Junior</option>
                  <option value="middle">Middle</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead / Principal</option>
                </select>
              </Field>
              <Field label="Required skills" required className="sm:col-span-2"
                hint="4–8 specific skills work better than a long list.">
                <ChipInput
                  ariaLabel="Required skills"
                  value={skills}
                  onChange={(v) => {
                    setSkills(v);
                    if (v.length) setSkillsInvalid(false);
                  }}
                  placeholder="Add skill and press Enter…"
                  invalid={skillsInvalid}
                />
              </Field>
              <Field label="Engagement" required>
                <select {...form.register("engagement")} className={fieldInputClass()}>
                  <option value="full_time">Full-time (40 h/week)</option>
                  <option value="part_time">Part-time (20 h/week)</option>
                  <option value="project_based">Project-based</option>
                </select>
              </Field>
              <Field label="Location">
                <input
                  type="text"
                  placeholder="e.g. Remote — Europe"
                  {...form.register("location")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Budget (USD / month)">
                <input
                  type="text"
                  placeholder="e.g. $7,000 – $9,000"
                  {...form.register("budget")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Start date">
                <input
                  type="text"
                  placeholder="e.g. May 15, 2026"
                  {...form.register("start_date")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field
                label="Project description"
                required
                error={form.formState.errors.description?.message}
                className="sm:col-span-2"
              >
                <textarea
                  placeholder="Describe what the team is building, what success looks like, and any context that would help us match the right person."
                  {...form.register("description")}
                  className={`${fieldInputClass(!!form.formState.errors.description)} min-h-[110px] resize-y`}
                />
              </Field>
            </div>

            {topError && (
              <div className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-[#b91c1c]">
                {topError}
              </div>
            )}

            <div className="mt-5 flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-end">
              <Link to="/" className={`${buttonClass({ variant: "secondary" })} w-full sm:w-auto`}>
                Cancel
              </Link>
              <Button type="submit" loading={form.formState.isSubmitting} className="w-full sm:w-auto">
                Submit request
              </Button>
            </div>
          </Card>
        </div>

        <aside className="flex flex-col gap-4 lg:sticky lg:top-[88px]">
          <Card title="What happens next">
            <ol className="flex flex-col gap-3 text-[13px]">
              <Step n={1}>We refine the request and reply with any clarifying questions.</Step>
              <Step n={2}>We open the role to vetted specialists in our network.</Step>
              <Step n={3}>3–5 candidate profiles delivered to interview directly.</Step>
            </ol>
          </Card>
        </aside>
      </form>
    </div>
  );
}

function Card({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-6">
      <h2 className="mb-1 text-base font-semibold">{title}</h2>
      {hint && <p className="mb-5 text-[13px] text-text-muted">{hint}</p>}
      {children}
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="grid size-[22px] shrink-0 place-items-center rounded-full bg-primary-soft text-[11px] font-bold text-primary-hover">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}
